// =============================================================================
// ANALYTICS SERVICE - Google Analytics Event Tracking
// =============================================================================

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

type GamePhase = 'landing' | 'intro' | 'game' | 'ending' | 'gameOver' | 'share' | 'share-petition' | 'create-game' | 'rules';

const GAME_MODE = 'balanced' as const;

interface ResourceLevels {
  militaryStrength: number;
  economy: number;
  resilience: number;
  westernSupport: number;
  trust: number;
}

/**
 * Send an event to Google Analytics
 */
function sendEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
}

// =============================================================================
// PAGE VIEW TRACKING (SPA)
// =============================================================================

const PAGE_TITLES: Record<GamePhase, string> = {
  landing: 'Landing | Головнокомандувач',
  intro: 'Intro | Головнокомандувач',
  game: 'Game | Головнокомандувач',
  ending: 'Victory | Головнокомандувач',
  gameOver: 'Game Over | Головнокомандувач',
  share: 'Share Donation | Головнокомандувач',
  'share-petition': 'Share Petition | Головнокомандувач',
  'create-game': 'Create Game | Головнокомандувач',
  rules: 'Rules | Головнокомандувач',
};

/**
 * Track page view for SPA navigation
 */
export function trackPageView(phase: GamePhase) {
  sendEvent('page_view', {
    page_title: PAGE_TITLES[phase],
    page_location: window.location.href,
    page_path: `/${phase}`,
  });
}

// =============================================================================
// GAME EVENTS
// =============================================================================

/**
 * Track when user starts the game
 */
export function trackGameStart(
  source: 'intro' | 'resume' | 'landing',
  hasCampaign: boolean,
) {
  sendEvent('game_start', {
    source,
    has_campaign: hasCampaign,
    game_mode: GAME_MODE,
  });
}

/**
 * Track each decision/question answered
 */
export function trackDecision(params: {
  decisionNumber: number;
  maxDecisions: number;
  optionIndex: number;
  timeSpentSeconds: number;
  resourceLevels: ResourceLevels;
}) {
  const progressPercent = Math.round(
    (params.decisionNumber / params.maxDecisions) * 100
  );

  sendEvent('decision', {
    decision_number: params.decisionNumber,
    max_decisions: params.maxDecisions,
    option_index: params.optionIndex,
    time_spent_seconds: params.timeSpentSeconds,
    progress_percent: progressPercent,
    game_mode: GAME_MODE,
    ...flattenResourceLevels(params.resourceLevels),
  });
}

/**
 * Track game completion (all questions answered)
 */
export function trackGameComplete(params: {
  finalStats: ResourceLevels;
  totalTimeSeconds: number;
}) {
  const averageScore = calculateAverageScore(params.finalStats);

  sendEvent('game_complete', {
    average_score: averageScore,
    total_time_seconds: params.totalTimeSeconds,
    game_mode: GAME_MODE,
    ...flattenResourceLevels(params.finalStats),
  });
}

/**
 * Track game over (critical failure)
 */
export function trackGameOver(params: {
  type: string;
  decisionNumber: number;
  finalStats: ResourceLevels;
}) {
  const failedResource = findFailedResource(params.finalStats);

  sendEvent('game_over', {
    type: params.type,
    decision_number: params.decisionNumber,
    failed_resource: failedResource,
    game_mode: GAME_MODE,
    ...flattenResourceLevels(params.finalStats),
  });
}

/**
 * Track when user exits/abandons the game
 */
export function trackPageExit(params: {
  decisionNumber: number;
  phase: GamePhase;
  resourceLevels: ResourceLevels;
}) {
  sendEvent('page_exit', {
    decision_number: params.decisionNumber,
    phase: params.phase,
    game_mode: GAME_MODE,
    ...flattenResourceLevels(params.resourceLevels),
  });
}

/**
 * Track when user restarts the game
 */
export function trackRetry(fromPhase: GamePhase, lastDecision: number) {
  sendEvent('retry', {
    from_phase: fromPhase,
    last_decision: lastDecision,
  });
}

// =============================================================================
// CAMPAIGN & ACTION STRIP EVENTS
// =============================================================================

/**
 * Track when campaign is loaded from short link or subdomain
 */
export function trackCampaignLoad(campaignType: 'donation' | 'petition' | 'subdomain', campaignRef: string) {
  sendEvent('campaign_load', {
    campaign_type: campaignType,
    campaign_id: campaignRef,
  });
}

/**
 * Track action strip visibility
 */
export function trackActionStripView(decisionNumber: number, campaignType: 'donation' | 'petition') {
  sendEvent('action_strip_view', {
    decision_number: decisionNumber,
    campaign_type: campaignType,
  });
}

/**
 * Track action strip CTA click
 */
export function trackActionStripClick(campaignType: 'donation' | 'petition') {
  sendEvent('action_strip_click', {
    campaign_type: campaignType,
  });
}

/**
 * Track action strip dismiss
 */
export function trackActionStripDismiss(decisionNumber: number) {
  sendEvent('action_strip_dismiss', {
    decision_number: decisionNumber,
  });
}

// =============================================================================
// SOCIAL SHARING
// =============================================================================

type SharePlatform = 'twitter' | 'facebook' | 'telegram' | 'threads' | 'instagram' | 'clipboard' | 'native';
type ShareType = 'victory' | 'stalemate' | 'defeat' | 'gameOver';

/**
 * Track social sharing action
 */
export function trackShare(platform: SharePlatform, shareType: ShareType, score?: number) {
  sendEvent('share', {
    method: platform,
    content_type: shareType,
    score: score ? Math.round(score) : undefined,
  });
}

// =============================================================================
// FEEDBACK EVENTS
// =============================================================================

type FeedbackGameResult = 'victory' | 'stalemate' | 'defeat' | 'gameOver';

/**
 * Track feedback submission
 */
export function trackFeedback(rating: number, gameResult: FeedbackGameResult, hasMessage: boolean) {
  sendEvent('feedback_submit', {
    rating,
    game_result: gameResult,
    has_message: hasMessage,
  });
}

/**
 * Track feedback modal opened
 */
export function trackFeedbackModalOpen(gameResult: FeedbackGameResult) {
  sendEvent('feedback_modal_open', {
    game_result: gameResult,
  });
}

/**
 * Track feedback modal dismissed without submitting
 */
export function trackFeedbackModalDismiss(gameResult: FeedbackGameResult) {
  sendEvent('feedback_modal_dismiss', {
    game_result: gameResult,
  });
}

// =============================================================================
// HELPERS
// =============================================================================

function flattenResourceLevels(resources: ResourceLevels): Record<string, number> {
  return {
    military_strength: Math.round(resources.militaryStrength),
    economy: Math.round(resources.economy),
    resilience: Math.round(resources.resilience),
    western_support: Math.round(resources.westernSupport),
    trust: Math.round(resources.trust),
  };
}

function calculateAverageScore(stats: ResourceLevels): number {
  const { militaryStrength, economy, resilience, westernSupport, trust } = stats;
  return Math.round((militaryStrength + economy + resilience + westernSupport + trust) / 5);
}

function findFailedResource(stats: ResourceLevels): string | null {
  const entries = Object.entries(stats) as [keyof ResourceLevels, number][];
  const failed = entries.find(([, value]) => value <= 0);
  return failed ? failed[0] : null;
}
